import { useAuthStore, SESSION_FLAG_KEY } from '../infrastructure/auth-store';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: string;
  organizationId: string;
  clinicIds: string[];
}

export interface SessionClinic {
  clinicId: string;
  clinicName: string;
  status?: string;
}

export function useSession(): {
  user: SessionUser | null;
  isAuthenticated: boolean;
  isMfaEnrolled: boolean;
  activeClinic: SessionClinic | null;
  assignedClinics: SessionClinic[];
  isSwitchingClinic: boolean;
  accessToken: string | null;
  login: (
    email: string,
    password: string,
  ) => Promise<import('../infrastructure/auth-store').LoginResult>;
  logout: () => void;
  selectClinic: (clinicId: string) => Promise<void>;
  loadCurrentUser: () => Promise<void>;
  hydrateFromStorage: () => void;
  silentRefresh: () => Promise<void>;
  updateUserName: (name: string) => void;
  setMfaEnrolled: (enrolled: boolean) => void;
  syncClinicsFromProfile: (
    profile: import('../infrastructure/auth-store').CurrentUserApiResponse,
  ) => void;
} {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const isMfaEnrolled = useAuthStore((s) => s.isMfaEnrolled);
  const activeClinic = useAuthStore((s) => s.activeClinic);
  const assignedClinics = useAuthStore((s) => s.assignedClinics);
  const isSwitchingClinic = useAuthStore((s) => s.isSwitchingClinic);
  const login = useAuthStore((s) => s.login);
  const logout = useAuthStore((s) => s.logout);
  const selectClinic = useAuthStore((s) => s.selectClinic);
  const loadCurrentUser = useAuthStore((s) => s.loadCurrentUser);
  const hydrateFromStorage = useAuthStore((s) => s.hydrateFromStorage);
  const silentRefresh = useAuthStore((s) => s.silentRefresh);
  const syncClinicsFromProfile = useAuthStore((s) => s.syncClinicsFromProfile);

  function updateUserName(name: string): void {
    useAuthStore.setState((state) => ({
      user: state.user ? { ...state.user, name } : state.user,
    }));
  }

  function setMfaEnrolled(enrolled: boolean): void {
    useAuthStore.setState({ isMfaEnrolled: enrolled });
  }

  return {
    user,
    isAuthenticated: !!accessToken,
    isMfaEnrolled,
    activeClinic,
    assignedClinics,
    isSwitchingClinic,
    accessToken,
    login,
    logout,
    selectClinic,
    loadCurrentUser,
    hydrateFromStorage,
    silentRefresh,
    updateUserName,
    setMfaEnrolled,
    syncClinicsFromProfile,
  };
}

export function useMfaSession(): {
  mfaSessionToken: string | null;
  setTokens: (accessToken: string) => void;
  completeMfa: (code: string) => Promise<void>;
} {
  const mfaSessionToken = useAuthStore((s) => s.mfaSessionToken);
  const setTokens = useAuthStore((s) => s.setTokens);
  const completeMfa = useAuthStore((s) => s.completeMfa);
  return { mfaSessionToken, setTokens, completeMfa };
}

export { SESSION_FLAG_KEY };
